<template>
    <v-bottom-sheet v-model="dialog">
        <v-sheet v-if="loading" class="white d-flex flex-column align-center justify-center pa-8" height="50vh">
            <span class="text-subtitle-1">Cargando información</span>
            <v-progress-linear indeterminate color="primary" class="my-8"></v-progress-linear>
        </v-sheet>

        <v-sheet v-else class="white">
            <div class="d-flex align-center blue-grey darken-4 px-8" style="height: 64px">
                <span class="text-subtitle-1 white--text">Historial del elemento</span>
                <v-text-field dark flat solo-inverted hide-details clearable prepend-inner-icon="mdi-magnify" label="Filtrar el historial ..." class="px-8" v-model="searchText" />
                <v-btn small dark color="red" @click="cancel"><v-icon>mdi-close</v-icon></v-btn>
            </div>
            <perfect-scrollbar>
                <v-timeline v-if="filteredResults.length > 0" dense clipped style="max-width: 99%">
                    <template v-for="group in grupedResults">
                        <v-timeline-item :key="group.date" class="my-0 py-2" icon="mdi-calendar" fill-dot>
                            <span class="text-h6 font-weight-bold">{{ niceDate(group.date) }}</span>
                        </v-timeline-item>

                        <v-timeline-item v-for="(entry, i) in group.entries" :key="`${group.date}-${i}`" class="hoverable py-4" color="grey" icon-color="grey lighten-2" small fill-dot icon="mdi-star">
                            <v-row justify="space-between" dense>
                                <v-col cols="12" class="d-flex align-center font-weight-bold">
                                    <span>{{ entry.message }}</span>
                                    <v-spacer />
                                    <span class="grey--text text--darken-3 mr-1">{{ entry.user }}</span>
                                    <span class="grey--text text--darken-1 mr-4">a las {{ entry.time }}</span>
                                </v-col>
                                <v-col v-if="entry.data" cols="12" class="py-0 text-caption" style="white-space: pre-line">
                                    {{ entry.data }}
                                </v-col>
                            </v-row>
                        </v-timeline-item>
                    </template>
                </v-timeline>
                <div v-else class="d-flex flex-column align-center justify-center py-8">
                    <span class="text-h6 font-weight-bold red--text text--darken-2">No hay registros</span>
                </div>
            </perfect-scrollbar>
        </v-sheet>
    </v-bottom-sheet>
</template>

<style scoped lang="scss">
.ps {
    height: 50vh;
    overflow-x: hidden;
}

.hoverable {
    cursor: pointer;
    &:hover {
        background-color: rgba(0, 0, 0, 0.1);
    }
}
</style>

<script lang="ts">
import { EventBus } from "@/components/EventBus";
import { OpenLogViewerRequest, DataClient } from "@/services/DataClient";
import { SearchLogsResponse } from "@/services/ClientInterfaces";
import { Utilities } from "@/services/Utilities";
import { Component, Vue, Watch } from "vue-property-decorator";
import { BaseView } from "@/components/BaseView";
import { PerfectScrollbar } from "vue2-perfect-scrollbar";

interface InternalItem {
    date: string;
    entries: Array<{
        time: string;
        user: string;
        message: string;
        data: string;
    }>;
}

@Component
export default class extends BaseView {
    dialog = false;
    loading = false;

    query = "";
    searching = false;

    selectedType = "";
    selectedId = "";

    searchText = "";

    results: SearchLogsResponse = [];

    mounted() {
        EventBus.$on(DataClient.Logs.OpenLogViewerDialogEvent, this.handleOpenEvent);
    }

    beforeDestroy() {
        EventBus.$off(DataClient.Logs.OpenLogViewerDialogEvent, this.handleOpenEvent);
    }

    async handleOpenEvent(event: OpenLogViewerRequest) {
        if (this.dialog) {
            this.dialog = false;
            return;
        }

        this.results = [];

        this.searchText = "";
        this.loading = true;
        this.dialog = true;

        this.results = await DataClient.Logs.search({
            aggregateId: event.aggregateId,
        });

        this.loading = false;
    }

    get filteredResults(): SearchLogsResponse {
        const normalizedSearchText = Utilities.normalize(this.searchText);
        const filteredResults = this.searchText ? this.results.filter((item) => Utilities.objectIncludes(item, ["user", "message", "data"], normalizedSearchText)) : this.results;

        return filteredResults;
    }

    get grupedResults(): InternalItem[] {
        // group results by date
        const groups: InternalItem[] = [];

        for (const result of this.filteredResults) {
            const group = groups.find((g) => g.date === result.date);

            if (group) {
                group.entries.push({
                    time: result.time,
                    user: result.user,
                    message: result.message,
                    data: result.data,
                });
            } else {
                groups.push({
                    date: result.date,
                    entries: [
                        {
                            time: result.time,
                            user: result.user,
                            message: result.message,
                            data: result.data,
                        },
                    ],
                });
            }
        }

        return groups;
    }

    accept() {
        this.dialog = false;
        this.results = [];

        DataClient.Logs.open(true, true);
    }

    cancel() {
        this.dialog = false;
        this.results = [];

        DataClient.Logs.open(false, true);
    }

    niceDate(date: string): string {
        if (date === Utilities.todayAsYYYYMMDD()) {
            return "Hoy, " + Utilities.getDayOfWeekNameYYYYMMDD(date).toLowerCase() + " " + Utilities.formatDateToDDMMYYYY(date);
        } else if (date === Utilities.yesterdayAsYYYYMMDD()) {
            return "Ayer, " + Utilities.getDayOfWeekNameYYYYMMDD(date).toLowerCase() + " " + Utilities.formatDateToDDMMYYYY(date);
        } else {
            return Utilities.getDayOfWeekNameYYYYMMDD(date) + " " + Utilities.formatDateToDDMMYYYY(date);
        }
    }
}
</script>
